import React, {useState, useEffect, useRef} from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  Platform,
  Animated,
  Image,
} from 'react-native';
import {useAuth} from '../contexts/AuthContext';
import {Fonts} from '../config/fonts';

const LoginScreen: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const {signIn, signInWithOAuth, loading} = useAuth();
  const noiseAnimation = useRef(new Animated.Value(0)).current;

  // Animation de texture papier très subtile
  useEffect(() => {
    const animateNoise = () => {
      Animated.loop(
        Animated.sequence([
          Animated.timing(noiseAnimation, {
            toValue: 1,
            duration: 30000, // 30 secondes par cycle
            useNativeDriver: false,
          }),
          Animated.timing(noiseAnimation, {
            toValue: 0,
            duration: 30000,
            useNativeDriver: false,
          }),
        ]),
      ).start();
    };
    animateNoise();
  }, [noiseAnimation]);

  const handleLogin = async () => {
    if (!email || !password) {
      return;
    }
    try {
      await signIn(email, password);
    } catch (error: any) {
      console.error('Login error:', error);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      await signInWithOAuth('google');
    } catch (error: any) {
      console.error('Google login error:', error);
    }
  };

  const handleFacebookLogin = async () => {
    try {
      await signInWithOAuth('facebook');
    } catch (error: any) {
      console.error('Facebook login error:', error);
    }
  };

  // Générer le SVG de noise pour la texture papier (animation très subtile)
  const noiseSVG = Platform.OS === 'web' ? (
    <View style={styles.noiseContainer}>
      <Animated.View
        style={[
          styles.noiseOverlay,
          {
            opacity: noiseAnimation.interpolate({
              inputRange: [0, 1],
              outputRange: [0.02, 0.04], // Opacité 2-4%
            }),
            transform: [
              {
                translateX: noiseAnimation.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0, 1],
                }),
              },
              {
                translateY: noiseAnimation.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0, 1],
                }),
              },
            ],
          },
        ]}>
        <View style={styles.noisePattern} />
      </Animated.View>
    </View>
  ) : null;

  // En mode web : pas de login (l'éditeur fournit le token) — message rigolo
  if (Platform.OS === 'web') {
    return (
      <View style={styles.container}>
        <View style={styles.webMessageWrapper}>
          <Image
            source={{uri: '/img/logo_title.png'}}
            style={styles.logoImage}
            resizeMode="contain"
          />
          <Text style={styles.webMessageTitle}>Pas de login par ici !</Text>
          <Text style={styles.webMessageText}>
            La version web est pilotée par l’éditeur. Ton aventure t’attend de l’autre côté du miroir — ouvre-la depuis l’éditeur pour jouer.
          </Text>
          <Text style={styles.webMessageSub}>(Et non, ce n’est pas un portail secret.)</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Animation de texture papier en fond */}
      {noiseSVG}
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled">
        <View style={styles.cardContainer}>
          <View style={styles.card}>
            <View style={styles.logoContainer}>
              <Image
                source={
                  Platform.OS === 'web'
                    ? {uri: '/img/logo_title.png'}
                    : require('../assets/img/logo_title.png')
                }
                style={styles.logoImage}
                resizeMode="contain"
              />
            </View>

            <TextInput
              style={styles.input}
              placeholder="Email"
              placeholderTextColor="#8B7355"
              value={email}
              onChangeText={setEmail}
              keyboardType="email-address"
              autoCapitalize="none"
              editable={!loading}
            />

            <TextInput
              style={styles.input}
              placeholder="Mot de passe"
              placeholderTextColor="#8B7355"
              value={password}
              onChangeText={setPassword}
              secureTextEntry
              editable={!loading}
            />

            <TouchableOpacity
              style={[styles.loginButton, loading && styles.buttonDisabled]}
              onPress={handleLogin}
              disabled={loading}
              activeOpacity={0.9}>
              {loading ? (
                <ActivityIndicator color="#F5F3ED" />
              ) : (
                <Text style={styles.loginButtonText}>Entrer dans l'aventure</Text>
              )}
            </TouchableOpacity>

            <View style={styles.divider}>
              <View style={styles.dividerLine} />
              <Text style={styles.dividerText}>ou</Text>
              <View style={styles.dividerLine} />
            </View>

            <TouchableOpacity
              style={[styles.socialButton, styles.googleButton, loading && styles.buttonDisabled]}
              onPress={handleGoogleLogin}
              disabled={loading}
              activeOpacity={0.8}>
              <Text style={styles.socialButtonText}>Continuer avec Google</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.socialButton, styles.facebookButton, loading && styles.buttonDisabled]}
              onPress={handleFacebookLogin}
              disabled={loading}
              activeOpacity={0.8}>
              <Text style={styles.socialButtonText}>Continuer avec Facebook</Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    ...Platform.select({
      web: {
        minHeight: '100vh' as any,
      },
    }),
    backgroundColor: '#F5F3ED', // Fond papier beige chaud
    position: 'relative',
  },
  webMessageWrapper: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
    maxWidth: 420,
    alignSelf: 'center',
  },
  webMessageTitle: {
    fontFamily: Fonts.script,
    fontSize: 28,
    color: '#5C4033',
    marginTop: 24,
    textAlign: 'center',
  },
  webMessageText: {
    fontFamily: Fonts.body,
    fontSize: 16,
    color: '#5C4033',
    marginTop: 16,
    textAlign: 'center',
    lineHeight: 24,
  },
  webMessageSub: {
    fontFamily: Fonts.body,
    fontSize: 14,
    color: '#8B7355',
    marginTop: 12,
    fontStyle: 'italic',
    textAlign: 'center',
  },
  noiseContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 0,
    pointerEvents: 'none',
  },
  noiseOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
  },
  noisePattern: {
    width: '100%',
    height: '100%',
    ...(Platform.OS === 'web' && {
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.4'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'repeat',
      backgroundSize: '200px 200px',
    }),
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    padding: 20,
    zIndex: 1,
  },
  cardContainer: {
    alignItems: 'center',
  },
  card: {
    width: '100%',
    maxWidth: 420,
    padding: 32,
    backgroundColor: '#D4C4B0', // Papier sombre / carton
    ...Platform.select({
      web: {
        borderTopLeftRadius: 14,
        borderTopRightRadius: 10,
        borderBottomRightRadius: 16,
        borderBottomLeftRadius: 12,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)',
      },
      default: {
        borderRadius: 12,
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 2},
        shadowOpacity: 0.15,
        shadowRadius: 8,
        elevation: 4,
      },
    }),
  },
  logoContainer: {
    alignItems: 'center',
    marginBottom: 20,
    paddingVertical: 20,
  },
  logoImage: {
    width: 250,
    height: 80,
    ...Platform.select({
      web: {
        maxWidth: '100%',
      },
    }),
  } as any,
  input: {
    backgroundColor: '#F5F3ED',
    ...Platform.select({
      web: {
        borderTopLeftRadius: 8,
        borderTopRightRadius: 6,
        borderBottomRightRadius: 8,
        borderBottomLeftRadius: 6,
      },
      default: {
        borderRadius: 8,
      },
    }),
    padding: 16,
    marginBottom: 20,
    fontSize: 16,
    color: '#2C2C2C',
    fontFamily: Fonts.ui,
    borderWidth: 1,
    borderColor: '#C4B5A0',
  },
  loginButton: {
    backgroundColor: '#8B7355', // Brun / beige foncé
    ...Platform.select({
      web: {
        borderTopLeftRadius: 10,
        borderTopRightRadius: 8,
        borderBottomRightRadius: 12,
        borderBottomLeftRadius: 10,
      },
      default: {
        borderRadius: 10,
      },
    }),
    paddingVertical: 16,
    paddingHorizontal: 24,
    alignItems: 'center',
    marginTop: 8,
  },
  loginButtonText: {
    color: '#F5F3ED',
    fontSize: 17,
    fontWeight: '500',
    fontFamily: Fonts.reading,
    letterSpacing: 0.5,
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 24,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#C4B5A0',
    opacity: 0.5,
  },
  dividerText: {
    marginHorizontal: 16,
    fontSize: 14,
    color: '#6B5B4A',
    fontFamily: Fonts.ui,
    opacity: 0.7,
  },
  socialButton: {
    ...Platform.select({
      web: {
        borderTopLeftRadius: 8,
        borderTopRightRadius: 6,
        borderBottomRightRadius: 8,
        borderBottomLeftRadius: 6,
      },
      default: {
        borderRadius: 8,
      },
    }),
    paddingVertical: 14,
    paddingHorizontal: 20,
    alignItems: 'center',
    marginBottom: 12,
  },
  googleButton: {
    backgroundColor: '#B8866B', // Désaturé, brun chaud
  },
  facebookButton: {
    backgroundColor: '#8B9BA8', // Désaturé, gris bleuté
  },
  socialButtonText: {
    color: '#F5F3ED',
    fontSize: 15,
    fontWeight: '400',
    fontFamily: Fonts.ui,
  },
});

export default LoginScreen;
